// ============================================================================
// Facebook Messenger — Graph API response shapes + error classification.
//
// The Graph API returns success as `{ recipient_id, message_id }` and errors as
// `{ error: { message, type, code, error_subcode?, fbtrace_id? } }` — see
// https://developers.facebook.com/docs/graph-api/guides/error-handling.
//
// We classify the most common failure modes into a discriminated error class
// so callers (dispatcher, chat inbox) can react without re-parsing FB's
// subcode matrix:
//
//   - RATE_LIMITED  → back off + retry later (often paired with Retry-After)
//   - TOKEN_REVOKED → user removed the page install; refresh will not help,
//                      the instance must be re-authorized by a human
//   - PAGE_UNLINKED → page is no longer connected to our app (code 200
//                      + subcode 2018117) — same treatment as TOKEN_REVOKED
//                      for the caller; distinct here for analytics
//   - INVALID_INPUT → programmer error on OUR side (empty recipient etc.)
//                      — do NOT send to FB at all
//   - API_ERROR     → everything else (network 5xx, unexpected shape)
// ============================================================================

// ============================================================================
// Wire shapes — the minimum slice of the Graph API schema we care about.
// ============================================================================

export interface FacebookSendResponse {
  recipient_id: string
  message_id: string
}

export interface FacebookUserProfile {
  id: string
  first_name: string
  last_name: string
  /** Pre-signed CDN URL. Short-lived, so do not cache long-term. */
  profile_pic?: string
  /** IETF locale tag, e.g. `en_US`, `th_TH`. */
  locale?: string
}

export interface FacebookGraphError {
  message: string
  type: string
  code: number
  error_subcode?: number
  fbtrace_id?: string
}

export interface FacebookErrorEnvelope {
  error: FacebookGraphError
}

// ============================================================================
// FacebookMessengerError — discriminated by `code` so `switch` exhaustive.
// ============================================================================

export type FacebookMessengerErrorCode =
  | 'RATE_LIMITED'
  | 'TOKEN_REVOKED'
  | 'PAGE_UNLINKED'
  | 'API_ERROR'
  | 'INVALID_INPUT'

export interface FacebookMessengerErrorDetails {
  /** Raw FB code (if we got one from the envelope). */
  providerCode?: number
  /** Raw FB subcode (if any). */
  providerSubcode?: number
  /** FB trace id — useful for support tickets. */
  fbtraceId?: string
  /** HTTP status of the underlying request, if known. */
  httpStatus?: number
  /** Parsed Retry-After (ms) — only populated for RATE_LIMITED. */
  retryAfterMs?: number
}

export class FacebookMessengerError extends Error {
  public readonly name = 'FacebookMessengerError'
  constructor(
    public readonly code: FacebookMessengerErrorCode,
    message: string,
    public readonly details: FacebookMessengerErrorDetails = {},
  ) {
    super(message)
  }
}

// ============================================================================
// Helpers — classify / detect
// ============================================================================

/**
 * True iff the envelope looks like a token-revoked failure (code 190 covers
 * expired, revoked, and password-changed access tokens). The dispatcher uses
 * this to stop retrying and mark the instance as needing re-auth.
 */
export function isTokenRevoked(err: unknown): boolean {
  if (!(err instanceof FacebookMessengerError)) return false
  return err.code === 'TOKEN_REVOKED'
}

/**
 * True iff the envelope indicates the page itself is no longer linked to the
 * app (user uninstalled the FB app integration). Similar remediation to
 * TOKEN_REVOKED but distinct for monitoring.
 */
export function isPageUnlinked(err: unknown): boolean {
  if (!(err instanceof FacebookMessengerError)) return false
  return err.code === 'PAGE_UNLINKED'
}

/**
 * Parse a `Retry-After` header value (either delta-seconds or HTTP-date) and
 * return milliseconds. Returns `undefined` on unparseable input so callers
 * can fall back to a default backoff strategy.
 */
export function parseRetryAfterMs(headerValue: string | null | undefined): number | undefined {
  if (!headerValue) return undefined
  const trimmed = headerValue.trim()
  if (trimmed.length === 0) return undefined
  // Delta-seconds form: integer number of seconds.
  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber * 1000)
  }
  // HTTP-date form.
  const parsed = Date.parse(trimmed)
  if (Number.isFinite(parsed)) {
    const diff = parsed - Date.now()
    return diff > 0 ? diff : 0
  }
  return undefined
}

/**
 * Map a Graph API error envelope + HTTP status to a `FacebookMessengerError`.
 *
 * Decision tree:
 *   1. HTTP 429 OR subcode 2018 (rate-limit) → RATE_LIMITED
 *   2. code 190                               → TOKEN_REVOKED
 *   3. code 200 + specific page-unlink subcode (2018117 / known variants)
 *                                              → PAGE_UNLINKED
 *   4. everything else                         → API_ERROR
 */
export function classifyFacebookError(
  envelope: FacebookErrorEnvelope | null | undefined,
  httpStatus: number,
  retryAfterMs?: number,
): FacebookMessengerError {
  const graphError = envelope?.error
  const providerCode = graphError?.code
  const providerSubcode = graphError?.error_subcode
  const fbtraceId = graphError?.fbtrace_id
  // FB error messages often echo back the full request URL, which can
  // include `access_token=...`. Sanitise before it flows into thrown
  // `Error.message` where Workers tail logs would capture it verbatim.
  const rawMessage = graphError?.message ?? `Facebook Graph API error (HTTP ${httpStatus})`
  const message = sanitizeFacebookErrorMessage(rawMessage)

  const details: FacebookMessengerErrorDetails = {
    providerCode,
    providerSubcode,
    fbtraceId,
    httpStatus,
    retryAfterMs,
  }

  // Rate limiting: HTTP 429 OR the Graph-level "app request limit reached"
  // subcode 2018. The subcode can also appear on a 200-coded envelope.
  if (httpStatus === 429 || providerSubcode === 2018) {
    return new FacebookMessengerError('RATE_LIMITED', message, details)
  }

  // Token revoked / expired.
  if (providerCode === 190) {
    return new FacebookMessengerError('TOKEN_REVOKED', message, details)
  }

  // Page disconnected from the app (subcode 2018117 is the canonical one;
  // code 200 + matching subcode family => page no longer linked).
  if (providerCode === 200 && providerSubcode === 2018117) {
    return new FacebookMessengerError('PAGE_UNLINKED', message, details)
  }

  return new FacebookMessengerError('API_ERROR', message, details)
}

/**
 * Strip FB-shaped secrets out of an error message string before it leaves the
 * adapter. Matches the token / secret patterns FB commonly echoes back in its
 * verbose error envelopes ("Tried to fetch with access_token=EAA...", etc.).
 *
 * Kept in `types.ts` (rather than `oauth.ts#redactSecrets`) to avoid a cross-
 * file import cycle — the rules are identical; this is a narrow copy used
 * only by the error classifier. Keep the two patterns in sync.
 */
export function sanitizeFacebookErrorMessage(raw: string): string {
  return raw
    .replace(/(access_token=)[^&\s"']+/gi, '$1REDACTED')
    .replace(/(client_secret=)[^&\s"']+/gi, '$1REDACTED')
    .replace(/(fb_exchange_token=)[^&\s"']+/gi, '$1REDACTED')
    .replace(/(Bearer\s+)[A-Za-z0-9._\-|]+/gi, '$1REDACTED')
}
